import React, { useState } from 'react';
import { CrudPage } from '../../components/CrudPage';
import { GanttVisualizationProfileControllerService, type GanttVisualizationProfileDto } from '../../api';
import GanttProfileFormDialog from '../forms/GanttProfileFormDialog';
import { useTranslation } from 'react-i18next';
import { useErrorHandler } from '../../utils/errorHandler';
import { useToast } from '../../hooks/useToast';

// Type with required id for the CrudPage
type GanttProfileWithId = GanttVisualizationProfileDto & { id: string };

const GanttVisualizationProfilesPage: React.FC = () => {
  const { t } = useTranslation();
  const { handleError } = useErrorHandler();
  const { showToast } = useToast();
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = async (): Promise<GanttProfileWithId[]> => {
    try {
      const result = await GanttVisualizationProfileControllerService.findAll();
      return result.map(profile => ({
        ...profile,
        id: profile.uuid || ''
      }));
    } catch (error) {
      handleError(error, 'fetching gantt profiles');
      return [];
    }
  };

  const saveData = async (data: GanttProfileWithId): Promise<void> => {
    try {
      if (data.uuid) {
        // Update existing profile
        await GanttVisualizationProfileControllerService.update(data.uuid, data);
        showToast('success', t('gantt.profileUpdated', 'Profil aktualisiert'), '');
      } else {
        // Create new profile
        await GanttVisualizationProfileControllerService.create(data);
        showToast('success', t('gantt.profileCreated', 'Profil erstellt'), '');
      }
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      handleError(error, 'saving gantt profile');
      throw error;
    }
  };

  const deleteData = async (id: string): Promise<void> => {
    try {
      await GanttVisualizationProfileControllerService.delete(id);
      showToast('success', t('gantt.profileDeleted', 'Profil gelöscht'), '');
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      handleError(error, 'deleting gantt profile');
      throw error;
    }
  };

  // Custom renderer for visibility
  const visibilityRenderer = (props: any) => {
    const profile = props?.data || props?.rowData || props;
    const visibility = profile?.visibility || 'USER';
    const visibilityLabels: Record<string, string> = {
      'USER': t('gantt.visibility.user', 'Benutzer'),
      'MANDANT': t('gantt.visibility.mandant', 'Planungsbereich'),
      'GLOBAL': t('gantt.visibility.global', 'Global')
    };
    return <span>{visibilityLabels[visibility] || visibility}</span>;
  };

  // Custom renderer for default indicator
  const defaultRenderer = (props: any) => {
    const profile = props?.data || props?.rowData || props;
    if (profile?.isDefault) {
      return (
        <div className="flex items-center justify-center">
          <span className="text-green-600 font-semibold">✓</span>
        </div>
      );
    }
    return <span>-</span>;
  };

  return (
    <CrudPage<GanttProfileWithId, string>
      titleKey="headers.ganttVisualizationProfiles"
      gridMeta={[
        { 
          field: 'name', 
          headerKey: 'gantt.grid.field.name', 
          width: 250 
        },
        { 
          field: 'description', 
          headerKey: 'gantt.grid.field.description', 
          width: 300 
        },
        { 
          field: 'visibility', 
          headerKey: 'gantt.grid.field.visibility', 
          width: 120,
          align: 'Center',
          render: visibilityRenderer
        },
        { 
          field: 'isDefault', 
          headerKey: 'gantt.grid.field.isDefault', 
          width: 100,
          align: 'Center',
          render: defaultRenderer
        },
        { 
          field: 'createdByUsername', 
          headerKey: 'gantt.grid.field.createdBy', 
          width: 150 
        }
      ]}
      fetchData={fetchData}
      saveData={saveData}
      deleteData={deleteData}
      FormComponent={GanttProfileFormDialog}
      refreshKey={refreshKey}
      entityName="gantt-profile"
    />
  );
};

export default GanttVisualizationProfilesPage;

